import type { ComponentProps } from 'preact';
import type { JourneyLeg } from '../../../../arcticapi/types';
import { LineRoundel } from '../../../base/LineRoundel/LineRoundel';
import { SvgIcon } from '../../../base/Icon/SvgIcon';
import { getTransportModeIcon } from '../../../../transportModes';
import { buildStepSummaryItems, type StepSummaryItem } from '../formatters';
import styles from './StepSummary.module.css';

export interface StepSummaryProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  legs: JourneyLeg[];
}

function StepSummaryChip({ item }: { item: StepSummaryItem }) {
  if (item.kind === 'walk') {
    const [value, unit] = item.label.split(' ');

    return (
      <span className={`${styles.chip} ${styles.walkChip}`}>
        <SvgIcon
          className={styles.walkIcon}
          name="walkSlow"
          size={24}
          strokeWidth="1"
        />
        <span className={styles.walkLabel}>
          <span>{value}</span>
          <span>{unit}</span>
        </span>
      </span>
    );
  }

  return (
    <span className={styles.serviceChip}>
      <LineRoundel
        iconName={getTransportModeIcon(item.mode)}
        line={{
          name: item.label,
          colors: {
            background:
              item.colors?.background ?? 'var(--internal-color-surface-accent)',
            foreground:
              item.colors?.foreground ??
              'var(--internal-color-surface-accent-contrast)',
          },
        }}
      />
    </span>
  );
}

export function StepSummary({ legs, className, ...props }: StepSummaryProps) {
  const items = buildStepSummaryItems(legs);
  // TODO: callers shouldn't need to set a classname. used in JourneyPlanItinerarySummary.module.css to remove margin
  const classNames = [styles.stepSummary, className ?? '']
    .filter(Boolean)
    .join(' ');

  if (items.length === 0) {
    return null;
  }

  return (
    <div {...props} className={classNames}>
      <div className={styles.track}>
        <span aria-hidden="true" className={styles.endpoint} />
        <ol
          aria-label={items.map((item) => item.ariaLabel).join(', ')}
          className={styles.list}
        >
          {items.map((item) => (
            <li
              className={styles.item}
              key={item.key}
              aria-label={item.ariaLabel}
            >
              <StepSummaryChip item={item} />
            </li>
          ))}
        </ol>
        <span aria-hidden="true" className={styles.endpoint} />
      </div>
    </div>
  );
}
